import React from 'react';
import {useState} from 'react';
import classNames from 'classnames';
import {Flex, Title, Text, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {ChangeType} from '../components/enums';
import {StatusField} from '../utils/statuses';
import styles from '../styles/historyContent.module.scss';
import {getChangeLogText} from '../utils/changes';
import {WHITE_HEX, DISABLED_GREY_HEX} from '../consts';
import texts from '../../texts';
import {highlightText} from '../../../../../../_legacy/library/utils/highlightText';
import {openForm} from '../../../../actions/form';
import {startNewForm} from '../../../../actions/screen';
import {formsStore, Record} from '../../../../../../stores/Form';
import {startEntity} from '../../../../../../_shared/GWTBridge/invoke/genPage';
import {DetailsContent} from '../../Common/components/DetailsContent';
import {createDataTestId} from '../../../../../../utils/dataTestId';

type StatusChangeLog = {
    fieldName: string;
    currValue: string;
    prevValue?: string;
    color?: string;
};

type StatusWrapperProps = {
    status: StatusChange;
    enabledCols: string[];
    record: Record;
    children: JSX.Element;
};

export type StatusChange = {
    type: ChangeType;
    key: string;
    user: string;
    initials: string;
    time: string;
    changes?: StatusChangeLog[];
};

type ZoomableNameProps = {
    name: string;
    fieldId: number;
    isLegacyStore: boolean;
    searchQuery?: string;
};

type ListHeaderProps = {
    user: string;
    numOfChanges: number;
    isLegacyStore: boolean;
};

type AssigneeBulletProps = {change: StatusChangeLog; isLegacyStore: boolean};

export const Initials = ({initials}: {initials: string}) => {
    return (
        <Title
            componentClasses={styles.initials}
            skin="h4"
            textColor="primary-500"
            text={initials}
            {...createDataTestId(`card-initials`, true)}
        />
    );
};

const Time = ({time}: {time: string}) => {
    return (
        <Text skin="label-1" textColor="secondary-500" {...createDataTestId(`panel-time`, true)}>
            {time}
        </Text>
    );
};

const ZoomableName = ({name, fieldId, isLegacyStore, searchQuery = ''}: ZoomableNameProps) => {
    const zoomToUser = async () => {
        const zoomfname = formsStore.getActiveForm()?.columnsMap?.get(fieldId)?.Zoomfname;
        if (zoomfname) {
            if (isLegacyStore) {
                const targetForm = await openForm(zoomfname);
                const targetField = targetForm?.columns?.find(col => col.Key)?.Name;
                targetField && startEntity(targetForm.ename, targetField, name);
            } else {
                startNewForm(zoomfname, name);
            }
        }
    };

    return (
        <Text
            skin="paragraph-2"
            textColor="primary-500"
            tag="span"
            onClick={zoomToUser}
            componentClasses={styles.zoomableName}
        >
            {highlightText(name, searchQuery)}
        </Text>
    );
};

const ItemText = ({text, searchQuery = ''}: {text: string; searchQuery?: string}) => {
    return (
        <Text skin="paragraph-2" textColor="secondary-400" tag="span">
            {highlightText(text, searchQuery)}
        </Text>
    );
};

const FieldName = ({fieldName, searchQuery = ''}: {fieldName: string; searchQuery?: string}) => {
    return (
        <Text
            skin="paragraph-2"
            textColor="primary-700"
            tag="span"
            componentClasses={styles.fieldName}
        >
            {highlightText(fieldName, searchQuery)}
        </Text>
    );
};

const Value = ({value, searchQuery = ''}: {value: string; searchQuery?: string}) => {
    return (
        <Text skin="paragraph-2" textColor="primary-700" tag="span">
            {highlightText(value, searchQuery)}
        </Text>
    );
};

const StatusColor = ({fill}: {fill: string}) => {
    const borderColor = fill !== WHITE_HEX ? fill : DISABLED_GREY_HEX;
    return (
        <span
            className={styles.statusColor}
            style={{
                backgroundColor: fill,
                borderColor: borderColor
            }}
        />
    );
};

const ListHeader = ({user, numOfChanges, isLegacyStore}: ListHeaderProps) => {
    const preText: string = texts.changed_fields_count;
    const preTextParts = preText.split(/([XY])/g).filter(i => i);
    return (
        <Text tag="p" {...createDataTestId(`panel-list-header`, true)}>
            {preTextParts.map((part, i) => {
                switch (part) {
                    case 'X':
                        return (
                            <ZoomableName
                                name={user}
                                fieldId={StatusField.User}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );
                    case 'Y':
                        return <ItemText text={numOfChanges.toString()} key={i} />;
                    default:
                        return <ItemText text={part} key={i} />;
                }
            })}
        </Text>
    );
};

const StatusBullet = ({change}: {change: StatusChangeLog}) => {
    const statusBullet: string = texts.status_bullet;
    const statusTextParts = statusBullet.split(/([XY])/g).filter(i => i);
    return (
        <li className={styles.changesListItem}>
            {statusTextParts.map((part, i) => {
                switch (part) {
                    case 'X':
                        return <FieldName fieldName={change.fieldName} key={i} />;
                    case 'Y':
                        return (
                            <span key={i}>
                                {change.color && <StatusColor fill={change.color} />}
                                <Value value={change.currValue} />
                            </span>
                        );
                    default:
                        return <ItemText text={part} key={i} />;
                }
            })}
        </li>
    );
};

const AssigneeBullet = ({change, isLegacyStore}: AssigneeBulletProps) => {
    const chnageBullet = texts.change_bullet;
    const changeTextParts = chnageBullet.split(/([XYZ])/g).filter(i => i);
    return (
        <li className={styles.changesListItem}>
            {changeTextParts.map((part, i) => {
                switch (part) {
                    case 'X':
                        return <FieldName fieldName={change.fieldName} key={i} />;
                    case 'Y':
                        return (
                            <ZoomableName
                                name={change.prevValue}
                                fieldId={StatusField.Assignee}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );
                    case 'Z':
                        return (
                            <ZoomableName
                                name={change.currValue}
                                fieldId={StatusField.Assignee}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );
                    default:
                        return <ItemText text={part} key={i} />;
                }
            })}
        </li>
    );
};

export const ListItemWrapper = ({status, enabledCols, record, children}: StatusWrapperProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    return (
        <div className={styles.listItemWrapper} {...createDataTestId(`panel-card`, false)}>
            <Flex
                componentClasses={classNames(styles.statusListItem, {[styles.openDetails]: isOpen})}
                justifyContent="flex-start"
                alignItems="flex-start"
            >
                <Initials initials={status.initials} />
                <Flex
                    direction="column"
                    componentClasses={styles.statusData}
                    alignItems="flex-start"
                    justifyContent="space-between"
                >
                    {children}
                    <Time time={status.time} />
                </Flex>
                {!!enabledCols.length && (
                    <Icon
                        icon={isOpen ? PriorityIcon.arrowUpThin : PriorityIcon.arrowBottom}
                        iconColor="secondary-400"
                        onClick={() => setIsOpen(prev => !prev)}
                        componentClasses={styles.detailsBtn}
                    />
                )}
            </Flex>
            {isOpen && <DetailsContent enabledCols={enabledCols} record={record} />}
        </div>
    );
};

export const CreationStatus = (status: StatusChange, isLegacyStore: boolean) => {
    const baseText: string = texts.status_created;
    const textParts = baseText.split(/(X)/g).filter(i => i);
    return (
        <Text tag="p">
            {textParts.map((part, i) => {
                if (part === 'X') {
                    return (
                        <ZoomableName
                            name={status.user}
                            fieldId={StatusField.User}
                            isLegacyStore={isLegacyStore}
                            key={i}
                        />
                    );
                } else {
                    return <ItemText text={part} key={i} />;
                }
            })}
        </Text>
    );
};

export const ChangedStatus = (status: StatusChange, isLegacyStore: boolean) => {
    const baseText = texts.status_cahnged;
    const textParts = baseText.split(/([XWY])/g).filter(i => i);
    const change = status.changes[0];
    return change ? (
        <Text tag="p" {...createDataTestId(`panel-statuses`, true)}>
            {textParts.map((part, i) => {
                switch (part) {
                    case 'X':
                        return (
                            <ZoomableName
                                name={status.user}
                                fieldId={StatusField.User}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );
                    case 'W':
                        return <FieldName fieldName={change.fieldName} key={i} />;
                    case 'Y':
                        return (
                            <span key={i}>
                                {change.color && <StatusColor fill={change.color} />}
                                <Value value={change.currValue} />
                            </span>
                        );
                    default:
                        return <ItemText text={part} key={i} />;
                }
            })}
        </Text>
    ) : null;
};

export const AssigneeChange = (status: StatusChange, isLegacyStore: boolean) => {
    const baseText = texts.single_change_log;
    const textParts = baseText.split(/([XWYZ])/g).filter(i => i);
    const currChange = status.changes[0];
    return currChange ? (
        <Text tag="p" {...createDataTestId(`panel-assignee`, true)}>
            {textParts.map((part, i) => {
                switch (part) {
                    case 'X':
                        return (
                            <ZoomableName
                                name={status.user}
                                fieldId={StatusField.User}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );

                    case 'W':
                        return <FieldName fieldName={currChange.fieldName} key={i} />;

                    case 'Y':
                        return (
                            <ZoomableName
                                name={currChange.prevValue}
                                fieldId={StatusField.Assignee}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );

                    case 'Z':
                        return (
                            <ZoomableName
                                name={currChange.currValue}
                                fieldId={StatusField.Assignee}
                                isLegacyStore={isLegacyStore}
                                key={i}
                            />
                        );

                    default:
                        return <ItemText text={part} key={i} />;
                }
            })}
        </Text>
    ) : null;
};

export const StatusMultipleChanges = (status: StatusChange, isLegacyStore: boolean) => {
    return (
        <>
            <ListHeader
                user={status.user}
                numOfChanges={status.changes.length}
                isLegacyStore={isLegacyStore}
            />
            <ul className={styles.changesList} {...createDataTestId(`panel-multi-status`, false)}>
                {status.changes.map((change, idx) => {
                    if (change.color) {
                        return <StatusBullet change={change} key={idx} />;
                    } else {
                        return (
                            <AssigneeBullet
                                change={change}
                                isLegacyStore={isLegacyStore}
                                key={idx}
                            />
                        );
                    }
                })}
            </ul>
        </>
    );
};

export const LogChangeItem = ({
    userName,
    column,
    prevValue,
    currValue,
    searchQuery,
    isLegacyStore,
    userFieldId
}) => {
    const logText = getChangeLogText(prevValue, currValue);
    const textParts = logText?.split(/([XWYZ])/g).filter(i => i);

    return (
        <Text tag="p" {...createDataTestId(`log-item`, true)}>
            {textParts?.map((part, i) => {
                switch (part) {
                    case 'X':
                        return (
                            <ZoomableName
                                name={userName}
                                fieldId={userFieldId}
                                isLegacyStore={isLegacyStore}
                                searchQuery={searchQuery}
                                key={i}
                            />
                        );
                    case 'W':
                        return <FieldName fieldName={column} key={i} searchQuery={searchQuery} />;
                    case 'Y':
                        return <Value value={prevValue} key={i} searchQuery={searchQuery} />;
                    case 'Z':
                        return <Value value={currValue} key={i} searchQuery={searchQuery} />;
                    default:
                        return <ItemText text={part} key={i} searchQuery={searchQuery} />;
                }
            })}
        </Text>
    );
};
